import z from "zod";
import type { SunrunAccountId } from "@remi/types/puppeteer";
import type { ReportIngestFailureReason, SunrunReportIngestType } from "@remi/types/sunrun-report-ingest";

const sunrunErrorTypeValues = [
  "ADDRESS_NOT_FOUND",
  "ALL_TIME_FILTER_FAILED",
  "UNKNOWN",
  "MODAL_ELEMENT_NOT_FOUND",
  "INVALID_ACCOUNT_ID",
  "EXPORT_BUTTON_NOT_FOUND",
  "NO_MATCHING_FILES_FOUND",
  "NO_CSV_FILE_FOUND",
  "SUBMITTING_INVOICES_NOT_ALLOWED_FOR_BID_PROJECTS",
  "INVOICE_BUCKET_NOT_FOUND",
  "PROJECT_HAS_NO_BID",
  "PROJECT_NOT_FOUND",
  "UNSUPPORTED_PARTNER_FLOW",
  "NO_PARTNER_TASK_URL_FOUND",
  "COULD_NOT_DOWNLOAD_FILE_FROM_S3",
  "COULD_NOT_DETECT_FILE_TYPE",
  "WRITTEN_FILE_IS_ZERO_BYTES",
  "FILE_INPUT_NOT_FOUND",
  "COULD_NOT_CONFIRM_FILE_UPLOAD",
  "COULD_NOT_FIND_OPTION_VALUE",
  "NO_REQUEST_FOR_QUOTE_FOUND",
  "NO_HIC_FILE_FOUND",
  "WORK_PHOTOS_ALBUM_IFRAME_NOT_FOUND",
  "WORK_PHOTOS_ALBUM_STATE_UNREADABLE",
  "WORK_ORDER_ALBUM_PARSE_RETURNED_EMPTY",
  "TABLE_SELECT_ELEMENT_NOT_FOUND",
  "SUBMIT_BUTTON_NOT_FOUND",
  "EXPECTED_ANCHOR_ELEMENT",
  "COULD_NOT_GET_FSD_URL",
  "FAILED_TO_ACCEPT_DISPATCH",
  "FAILED_TO_SET_DECLINE_REASON",
  "INPUT_ELEMENT_NOT_FOUND",
  "TEXT_AREA_ELEMENT_NOT_FOUND",
  "NO_SCROLL_DIV_FOUND",
  "NO_TABLE_FOUND",
  "UNSUPPORTED_SUNRUN_ACCOUNT_ID",
  "ELEMENT_TEXT_VALIDATION_FAILED",
  "BUTTON_NOT_FOUND",
  "DROPDOWN_OPTION_NOT_FOUND",
  "ELEMENT_DOES_NOT_EXIST",
  "NO_VALUE_FOR_KEY",
  "PARTNER_TASK_STATUS_NOT_OPEN",
  "UNABLE_TO_FIND_PARTNER_TASK_NUMBER",
  "BID_NOT_FOUND",
  "COULD_NOT_FIND_UPLOAD_DOCUMENTS_PAGE",
  "FAILED_TO_PROCESS_DISPATCH",
  "UNABLE_TO_PARSE_COUNT_FROM_STATUS_TEXT",
  "UNABLE_TO_FIND_STATUS_INFO_ELEMENT",
  "COULD_NOT_FIND_SHARINPIX_IFRAME",
  "DECLINE_FLOW_NOT_COMPLETED",
  "SCHEDULING_VALIDATION_FAILED",
  "LOGIN_VALIDATION_FAILED",
  "SALESFORCE_SOAP_LOGIN_FAILED",
  "SALESFORCE_SOAP_LOGIN_RESPONSE_MISSING_LOGIN_RESPONSE_NODE",
  "MAX_SCROLL_TIMEOUTS_EXCEEDED",
  "WORK_ORDER_INVOICE_REPORT_EMPTY",
  "INVALID_SUNRUN_QUERY_INPUT",
  "FSD_REPORT_SCHEMA_DRIFT",
  "WRONG_REPORT_CSV_DOWNLOADED",
  "INVALID_FSD_NAME_FOR_URL",
  "NON_IMAGE_FILE_TYPED_AS_PHOTO",
  "REPORT_INGEST_CHUNK_FAILED",
  "REPORT_FINALIZE_FAILED",
  "PORTAL_CAPTURE_TASK_NOT_FOUND",
  "PORTAL_CAPTURE_FAILED",
  "PORTAL_CAPTURE_FILE_TAG_MISSING",
  "REPORT_INGEST_NOT_CONFIGURED",
] as const;

const sunrunErrorTypeSchema = z.enum(sunrunErrorTypeValues);
const SunrunErrorType = sunrunErrorTypeSchema.enum;
type SunrunErrorType = z.infer<typeof sunrunErrorTypeSchema>;

export type SunrunErrors =
  | { errorType: typeof SunrunErrorType.ADDRESS_NOT_FOUND; data: { entityId: string | undefined } }
  | { errorType: typeof SunrunErrorType.ALL_TIME_FILTER_FAILED; data: { reason: string } }
  | { errorType: typeof SunrunErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof SunrunErrorType.MODAL_ELEMENT_NOT_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.INVALID_ACCOUNT_ID; data: { accountId?: string } }
  | { errorType: typeof SunrunErrorType.EXPORT_BUTTON_NOT_FOUND; data: { reason?: string } | undefined }
  | { errorType: typeof SunrunErrorType.NO_MATCHING_FILES_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.NO_CSV_FILE_FOUND; data: { path: string | null } }
  | { errorType: typeof SunrunErrorType.SUBMITTING_INVOICES_NOT_ALLOWED_FOR_BID_PROJECTS; data: { projectId: number } }
  | { errorType: typeof SunrunErrorType.INVOICE_BUCKET_NOT_FOUND; data: { partnerFlow?: string } }
  | { errorType: typeof SunrunErrorType.PROJECT_HAS_NO_BID; data: { projectId: number } }
  | { errorType: typeof SunrunErrorType.PROJECT_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof SunrunErrorType.UNSUPPORTED_PARTNER_FLOW; data: { partnerFlow: string } }
  | { errorType: typeof SunrunErrorType.NO_PARTNER_TASK_URL_FOUND; data: { bidQuoteId?: number } }
  | { errorType: typeof SunrunErrorType.COULD_NOT_DOWNLOAD_FILE_FROM_S3; data: { fileId: string; quoteId?: string } }
  | { errorType: typeof SunrunErrorType.COULD_NOT_DETECT_FILE_TYPE; data: { fileId: string; bufferLength: number } }
  | { errorType: typeof SunrunErrorType.WRITTEN_FILE_IS_ZERO_BYTES; data: { tempFilePath: string } }
  | { errorType: typeof SunrunErrorType.FILE_INPUT_NOT_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.COULD_NOT_CONFIRM_FILE_UPLOAD; data: undefined }
  | { errorType: typeof SunrunErrorType.COULD_NOT_FIND_OPTION_VALUE; data: { option: string } }
  | { errorType: typeof SunrunErrorType.NO_REQUEST_FOR_QUOTE_FOUND; data: { projectId: number } }
  | { errorType: typeof SunrunErrorType.NO_HIC_FILE_FOUND; data: { projectId: number } }
  | { errorType: typeof SunrunErrorType.WORK_PHOTOS_ALBUM_IFRAME_NOT_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.WORK_PHOTOS_ALBUM_STATE_UNREADABLE; data: { photoCount: number } }
  | {
      errorType: typeof SunrunErrorType.WORK_ORDER_ALBUM_PARSE_RETURNED_EMPTY;
      data: { workOrderId: string; albumEntryCount: number };
    }
  | { errorType: typeof SunrunErrorType.TABLE_SELECT_ELEMENT_NOT_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.SUBMIT_BUTTON_NOT_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.EXPECTED_ANCHOR_ELEMENT; data: { receivedType: string } }
  | { errorType: typeof SunrunErrorType.COULD_NOT_GET_FSD_URL; data: { bidId: string } }
  | { errorType: typeof SunrunErrorType.FAILED_TO_ACCEPT_DISPATCH; data: undefined }
  | { errorType: typeof SunrunErrorType.FAILED_TO_SET_DECLINE_REASON; data: { reason: string } }
  | { errorType: typeof SunrunErrorType.INPUT_ELEMENT_NOT_FOUND; data: { name: string } }
  | { errorType: typeof SunrunErrorType.TEXT_AREA_ELEMENT_NOT_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.NO_SCROLL_DIV_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.NO_TABLE_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.UNSUPPORTED_SUNRUN_ACCOUNT_ID; data: { partnerAccountId: string } }
  | {
      errorType: typeof SunrunErrorType.ELEMENT_TEXT_VALIDATION_FAILED;
      data: { validationText: string; elementText: string };
    }
  | { errorType: typeof SunrunErrorType.BUTTON_NOT_FOUND; data: { text: string } }
  | { errorType: typeof SunrunErrorType.DROPDOWN_OPTION_NOT_FOUND; data: { optionText: string } }
  | { errorType: typeof SunrunErrorType.ELEMENT_DOES_NOT_EXIST; data: { selector: string } }
  | { errorType: typeof SunrunErrorType.NO_VALUE_FOR_KEY; data: { key: string } }
  | { errorType: typeof SunrunErrorType.PARTNER_TASK_STATUS_NOT_OPEN; data: { status: string } }
  | { errorType: typeof SunrunErrorType.UNABLE_TO_FIND_PARTNER_TASK_NUMBER; data: { url: string } }
  | { errorType: typeof SunrunErrorType.BID_NOT_FOUND; data: undefined }
  | { errorType: typeof SunrunErrorType.COULD_NOT_FIND_UPLOAD_DOCUMENTS_PAGE; data: undefined }
  | { errorType: typeof SunrunErrorType.FAILED_TO_PROCESS_DISPATCH; data: { fsDispatchId: string; error: string } }
  | { errorType: typeof SunrunErrorType.UNABLE_TO_PARSE_COUNT_FROM_STATUS_TEXT; data: { statusText: string } }
  | { errorType: typeof SunrunErrorType.UNABLE_TO_FIND_STATUS_INFO_ELEMENT; data: undefined }
  | { errorType: typeof SunrunErrorType.COULD_NOT_FIND_SHARINPIX_IFRAME; data: undefined }
  | { errorType: typeof SunrunErrorType.DECLINE_FLOW_NOT_COMPLETED; data: undefined }
  | { errorType: typeof SunrunErrorType.SCHEDULING_VALIDATION_FAILED; data: { reason: string } }
  | { errorType: typeof SunrunErrorType.LOGIN_VALIDATION_FAILED; data: { message: string } }
  | {
      errorType: typeof SunrunErrorType.SALESFORCE_SOAP_LOGIN_FAILED;
      data: {
        partnerAccountId: SunrunAccountId;
        message?: string;
        faultString?: string;
        faultCode?: string;
      };
    }
  | {
      errorType: typeof SunrunErrorType.SALESFORCE_SOAP_LOGIN_RESPONSE_MISSING_LOGIN_RESPONSE_NODE;
      data: {
        partnerAccountId: SunrunAccountId;
      };
    }
  | {
      errorType: typeof SunrunErrorType.MAX_SCROLL_TIMEOUTS_EXCEEDED;
      data: { consecutiveTimeouts: number };
    }
  | {
      errorType: typeof SunrunErrorType.WORK_ORDER_INVOICE_REPORT_EMPTY;
      data: { accountId: SunrunAccountId; maxAttempts: number };
    }
  | {
      errorType: typeof SunrunErrorType.INVALID_SUNRUN_QUERY_INPUT;
      data: { reason: string };
    }
  | {
      errorType: typeof SunrunErrorType.FSD_REPORT_SCHEMA_DRIFT;
      data: {
        reportType: string;
        accountId: SunrunAccountId;
        rawRowCount: number;
        actualHeaders: string[];
        expectedColumns: string[];
      };
    }
  | {
      errorType: typeof SunrunErrorType.WRONG_REPORT_CSV_DOWNLOADED;
      data: {
        reportType: string;
        accountId: SunrunAccountId;
        rawRowCount: number;
        identityColumn: string;
        actualHeaders: string[];
      };
    }
  | {
      errorType: typeof SunrunErrorType.INVALID_FSD_NAME_FOR_URL;
      data: { name: string };
    }
  | {
      errorType: typeof SunrunErrorType.NON_IMAGE_FILE_TYPED_AS_PHOTO;
      data: { projectId: number; fileIds: string[]; fileNames: string[] };
    }
  | {
      errorType: typeof SunrunErrorType.REPORT_INGEST_CHUNK_FAILED;
      data: {
        reportType: SunrunReportIngestType;
        accountId: SunrunAccountId;
        chunkIndex: number;
        chunkCount: number;
        failureReason: ReportIngestFailureReason;
        httpStatus: number | undefined;
        errorCode: string | undefined;
      };
    }
  | {
      errorType: typeof SunrunErrorType.REPORT_FINALIZE_FAILED;
      data: {
        reportType: SunrunReportIngestType;
        accountId: SunrunAccountId;
        runId: string;
        attempts: number | undefined;
        failureReason: ReportIngestFailureReason | undefined;
        httpStatus: number | undefined;
        blockReason: string | undefined;
      };
    }
  | { errorType: typeof SunrunErrorType.PORTAL_CAPTURE_TASK_NOT_FOUND; data: { bidId: number; taskId: string } }
  | { errorType: typeof SunrunErrorType.PORTAL_CAPTURE_FAILED; data: { bidId: number; bidRequestId: string } }
  | { errorType: typeof SunrunErrorType.PORTAL_CAPTURE_FILE_TAG_MISSING; data: { bidId: number } }
  | {
      errorType: typeof SunrunErrorType.REPORT_INGEST_NOT_CONFIGURED;
      data: {
        reportType: SunrunReportIngestType;
        accountId: SunrunAccountId;
        serverUrlConfigured: boolean;
        webhookSecretConfigured: boolean;
      };
    };

export const sunrunErrorDetails: Record<SunrunErrorType, { sentryDescription: string }> = {
  ADDRESS_NOT_FOUND: { sentryDescription: "Address not found" },
  ALL_TIME_FILTER_FAILED: { sentryDescription: "All Time filter could not be applied to Salesforce report" },
  UNKNOWN: { sentryDescription: "Unknown Sunrun Error" },
  MODAL_ELEMENT_NOT_FOUND: { sentryDescription: "Modal element not found" },
  INVALID_ACCOUNT_ID: { sentryDescription: "Invalid account ID" },
  EXPORT_BUTTON_NOT_FOUND: { sentryDescription: "Export button not found" },
  NO_MATCHING_FILES_FOUND: { sentryDescription: "No matching files found in the directory" },
  NO_CSV_FILE_FOUND: { sentryDescription: "No CSV file found in the directory" },
  SUBMITTING_INVOICES_NOT_ALLOWED_FOR_BID_PROJECTS: {
    sentryDescription: "Submitting invoices is not allowed for bid projects",
  },
  INVOICE_BUCKET_NOT_FOUND: { sentryDescription: "Invoice bucket not found for partner flow" },
  PROJECT_HAS_NO_BID: { sentryDescription: "Project has no bid" },
  PROJECT_NOT_FOUND: { sentryDescription: "Project not found" },
  UNSUPPORTED_PARTNER_FLOW: { sentryDescription: "Only Sunrun FSD partner flows are supported" },
  NO_PARTNER_TASK_URL_FOUND: { sentryDescription: "No Partner Task URL found for quote" },
  COULD_NOT_DOWNLOAD_FILE_FROM_S3: { sentryDescription: "Could not download file from S3" },
  COULD_NOT_DETECT_FILE_TYPE: { sentryDescription: "Could not detect file type from buffer" },
  WRITTEN_FILE_IS_ZERO_BYTES: { sentryDescription: "Written file is 0 bytes" },
  FILE_INPUT_NOT_FOUND: { sentryDescription: "File input not found" },
  COULD_NOT_CONFIRM_FILE_UPLOAD: { sentryDescription: "Could not confirm file upload" },
  COULD_NOT_FIND_OPTION_VALUE: { sentryDescription: "Could not find the value of the option" },
  NO_REQUEST_FOR_QUOTE_FOUND: { sentryDescription: "No request for quote found for project" },
  NO_HIC_FILE_FOUND: { sentryDescription: "No HIC file found for project" },
  WORK_PHOTOS_ALBUM_IFRAME_NOT_FOUND: {
    sentryDescription: "Could not find work photos album iframe in submit work order modal",
  },
  WORK_PHOTOS_ALBUM_STATE_UNREADABLE: {
    sentryDescription: "Could not tell whether the work photos album already has photos",
  },
  WORK_ORDER_ALBUM_PARSE_RETURNED_EMPTY: {
    sentryDescription: "Work order album entries returned no parseable file names",
  },
  TABLE_SELECT_ELEMENT_NOT_FOUND: { sentryDescription: "Could not find the table select element" },
  SUBMIT_BUTTON_NOT_FOUND: { sentryDescription: "Could not find the submit button" },
  EXPECTED_ANCHOR_ELEMENT: { sentryDescription: "Expected an anchor element" },
  COULD_NOT_GET_FSD_URL: { sentryDescription: "Could not get FSD url because bid request has no external URL" },
  FAILED_TO_ACCEPT_DISPATCH: { sentryDescription: "Failed to accept dispatch" },
  FAILED_TO_SET_DECLINE_REASON: { sentryDescription: "Failed to set decline reason" },
  INPUT_ELEMENT_NOT_FOUND: { sentryDescription: "Unable to find input element" },
  TEXT_AREA_ELEMENT_NOT_FOUND: { sentryDescription: "Unable to find text area element" },
  NO_SCROLL_DIV_FOUND: { sentryDescription: "No scrollDiv found" },
  NO_TABLE_FOUND: { sentryDescription: "No table found" },
  UNSUPPORTED_SUNRUN_ACCOUNT_ID: { sentryDescription: "Unsupported SunrunAccountId" },
  ELEMENT_TEXT_VALIDATION_FAILED: { sentryDescription: "Element text validation failed" },
  BUTTON_NOT_FOUND: { sentryDescription: "Button not found" },
  DROPDOWN_OPTION_NOT_FOUND: { sentryDescription: "Could not find dropdown option" },
  ELEMENT_DOES_NOT_EXIST: { sentryDescription: "Element does not exist" },
  NO_VALUE_FOR_KEY: { sentryDescription: "No value for key" },
  PARTNER_TASK_STATUS_NOT_OPEN: { sentryDescription: "Partner task status is not open" },
  UNABLE_TO_FIND_PARTNER_TASK_NUMBER: { sentryDescription: "Unable to find partner task number" },
  BID_NOT_FOUND: { sentryDescription: "Bid not found" },
  COULD_NOT_FIND_UPLOAD_DOCUMENTS_PAGE: { sentryDescription: "Could not find the upload documents page" },
  FAILED_TO_PROCESS_DISPATCH: { sentryDescription: "Failed to process dispatch" },
  UNABLE_TO_PARSE_COUNT_FROM_STATUS_TEXT: { sentryDescription: "Unable to parse count from status text" },
  UNABLE_TO_FIND_STATUS_INFO_ELEMENT: { sentryDescription: "Unable to find status info element" },
  COULD_NOT_FIND_SHARINPIX_IFRAME: { sentryDescription: "Could not find sharinpix iframe" },
  DECLINE_FLOW_NOT_COMPLETED: { sentryDescription: "Decline flow did not complete successfully" },
  SCHEDULING_VALIDATION_FAILED: { sentryDescription: "Scheduling validation failed" },
  LOGIN_VALIDATION_FAILED: { sentryDescription: "Login validation failed" },
  SALESFORCE_SOAP_LOGIN_FAILED: {
    sentryDescription: "Salesforce SOAP login failed",
  },
  [SunrunErrorType.SALESFORCE_SOAP_LOGIN_RESPONSE_MISSING_LOGIN_RESPONSE_NODE]: {
    sentryDescription: "Salesforce SOAP login response missing loginResponse node",
  },
  [SunrunErrorType.MAX_SCROLL_TIMEOUTS_EXCEEDED]: {
    sentryDescription: "Salesforce table scroll timed out repeatedly; page may be unresponsive",
  },
  [SunrunErrorType.WORK_ORDER_INVOICE_REPORT_EMPTY]: {
    sentryDescription: "Work order invoice report returned zero rows after all download attempts",
  },
  [SunrunErrorType.INVALID_SUNRUN_QUERY_INPUT]: {
    sentryDescription: "Sunrun SOQL query builder rejected an invalid input",
  },
  [SunrunErrorType.FSD_REPORT_SCHEMA_DRIFT]: {
    sentryDescription:
      "FSD report CSV is the right report but every row failed to map — likely a Sunrun rename of a gate column",
  },
  [SunrunErrorType.WRONG_REPORT_CSV_DOWNLOADED]: {
    sentryDescription: "Downloaded CSV is a different Sunrun report than the one requested",
  },
  [SunrunErrorType.INVALID_FSD_NAME_FOR_URL]: {
    sentryDescription: "FSD record name normalized to an empty path; cannot build dispatch URL",
  },
  [SunrunErrorType.NON_IMAGE_FILE_TYPED_AS_PHOTO]: {
    sentryDescription:
      "File on the completed-work-photos task is typed PHOTO but its extension is not an image — skipped from Sunrun upload",
  },
  [SunrunErrorType.REPORT_INGEST_CHUNK_FAILED]: {
    sentryDescription: "A chunk of Sunrun report rows could not be written to the Sunrun integration server",
  },
  [SunrunErrorType.REPORT_FINALIZE_FAILED]: {
    sentryDescription: "Rows missing from a complete Sunrun report could not be soft-deleted",
  },
  [SunrunErrorType.PORTAL_CAPTURE_TASK_NOT_FOUND]: {
    sentryDescription: "The task a Sunrun portal capture was queued for does not exist",
  },
  [SunrunErrorType.PORTAL_CAPTURE_FAILED]: {
    sentryDescription: "A Sunrun portal screenshot capture failed unexpectedly",
  },
  [SunrunErrorType.PORTAL_CAPTURE_FILE_TAG_MISSING]: {
    sentryDescription: "The SUNRUN_PORTAL_SCREENSHOT FileTag row is missing, so no portal screenshot can be saved",
  },
  [SunrunErrorType.REPORT_INGEST_NOT_CONFIGURED]: {
    sentryDescription: "The Sunrun integration server URL or webhook secret is not configured for report ingest",
  },
};
